import React, { useState, useMemo } from "react";
import {
  FaHome,
  FaExclamationTriangle,
  FaNewspaper,
  FaPeopleCarry,
  FaRobot,
  FaMoneyBillWave,
  FaWallet,
  FaFileInvoiceDollar,
  FaFileAlt,
  FaBuilding,
  FaClipboardList,
  FaHeadset,
  FaHardHat,
  FaCheckCircle,
  FaCalendarAlt,
  FaFolderOpen,
  FaChevronDown,
  FaChevronRight,
  FaUser,
  FaGavel,
  FaHandHoldingUsd,
} from "react-icons/fa";
import styles from "./Sidebar.module.css";
import { useTranslation } from "react-i18next";
import { useBuilding } from "../context/BuildingContext";

const Sidebar = ({ selected, setSelected, collapsed }) => {
  const [expanded, setExpanded] = useState({});
  // Cuando el edificio del residente no tiene amenities configurados, no
  // mostramos la entrada Amenities en el submenu Edificio (evita prometer
  // algo que no existe en ese edificio).
  const { selectedBuildingHasAmenities, userUnitsInBuilding, selectedBuilding, soloAlquilerEdificio } = useBuilding();
  const { t } = useTranslation("nav");

  // Asambleas: solo propietarios VIGENTES (la convocatoria y las actas son de
  // los propietarios actuales; el servidor aplica la misma regla).
  const isCurrentOwner = (userUnitsInBuilding || []).some(
    (u) => u.userRole === "Propietario" && !u.isHistorical
  );
  // "Cuentas del edificio" (saldos + movimientos de todos): solo propietarios
  // vigentes. El ex-propietario (unidad histórica, edificio de solo lectura)
  // lee solo sus pagos (reglas 0D) y la pantalla le quedaría vacía.
  const isOwner = isCurrentOwner && !selectedBuilding?.isReadOnly;

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const rawItems = [
    { 
      id: "inicio", 
      label: "Inicio", 
      icon: <FaHome />,
      description: "Panel principal"
    },
    {
      id: "problema",
      label: "Incidencias",
      icon: <FaExclamationTriangle />,
      description: "Reportar problemas"
    },
    {
      id: "pagos",
      label: "Finanzas",
      icon: <FaMoneyBillWave />,
      description: "Gestión financiera",
      subitems: [
        { id: "cuentas", label: "Estado de Cuenta", icon: <FaWallet />, description: "Estado de cuenta" },
        ...(isOwner
          ? [{ id: "cuentasedificio", label: "Cuentas del edificio", icon: <FaBuilding />, description: "Saldos y movimientos" }]
          : []),
        // Inquilino de una unidad "Alquiler del edificio": la pantalla es la del alquiler.
        soloAlquilerEdificio
          ? { id: "expensas", labelKey: "alquiler", label: "Alquiler", icon: <FaFileInvoiceDollar />, description: "Pago del alquiler" }
          : { id: "expensas", label: "Gastos Comunes", icon: <FaFileInvoiceDollar />, description: "Pagos del mes" },
        { id: "mispagos", label: "Mis Pagos", icon: <FaWallet />, description: "Pagar todo junto" },
        { id: "documentos", label: "Comprobantes", icon: <FaFileAlt />, description: "Facturas y recibos" },
        { id: "multasresidente", label: "Multas", icon: <FaGavel />, description: "Multas y sanciones" },
        { id: "gastosvecinos", label: "Gastos del edificio", icon: <FaHandHoldingUsd />, description: "Cargá lo que pagaste" },
      ],
    },
    {
      id: "edificios",
      label: "Edificio",
      icon: <FaBuilding />,
      description: "Información del edificio",
      subitems: [
        ...(isCurrentOwner
          ? [{ id: "reuniones", label: "Asambleas", icon: <FaClipboardList />, description: "Convocatorias y actas" }]
          : []),
        { id: "misconsultas", label: "Mis Consultas", icon: <FaHeadset />, description: "Con la administración" },
        { id: "obras", label: "Obras y Proyectos", icon: <FaHardHat />, description: "Trabajos en curso" },
        { id: "revisiones", label: "Revisiones", icon: <FaCheckCircle />, description: "Inspecciones" },
        { id: "amenities", label: "Amenities", icon: <FaCalendarAlt />, description: "Salón, quincho, etc." },
        { id: "documentacion", label: "Documentación", icon: <FaFolderOpen />, description: "Reglamentos y manuales" },
      ],
    },
    { 
      id: "novedades", 
      label: "Novedades", 
      icon: <FaNewspaper />,
      description: "Últimas noticias"
    },
    { 
      id: "prestadores", 
      label: "Proveedores", 
      icon: <FaPeopleCarry />,
      description: "Servicios externos"
    },
    {
      id: "habi",
      label: "Habi",
      icon: <FaRobot />,
      description: "Asistente inteligente"
    },
  ];

  // Filtrar subitem "amenities" cuando el edificio no tiene amenities
  // configurados. Cuando el contexto aun no resolvio (null), mostramos
  // (preferimos falso positivo a esconder algo que el residente espera).
  const items = useMemo(() => {
    if (selectedBuildingHasAmenities !== false) return rawItems;
    return rawItems.map((item) => {
      if (!item.subitems) return item;
      return {
        ...item,
        subitems: item.subitems.filter((sub) => sub.id !== "amenities"),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuildingHasAmenities, isOwner, isCurrentOwner, soloAlquilerEdificio]);

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
      <nav className={styles.menu}>
        {items.map((item) => (
          <div key={item.id} className={styles.menuGroup}>
            <div
              onClick={() =>
                item.subitems
                  ? collapsed
                    ? setSelected(item.subitems[0].id)
                    : toggleExpand(item.id)
                  : setSelected(item.id)
              }
              className={`${styles.menuItem} ${
                selected === item.id || (item.subitems && item.subitems.some(sub => sub.id === selected)) ? styles.activeItem : ""
              } ${item.subitems ? styles.hasSubmenu : ""}`}
              title={collapsed ? t(`items.${item.id}.label`, item.label) : undefined}
            >
              <div className={styles.menuItemContent}>
                <div className={styles.menuItemIcon}>{item.icon}</div>
                <div className={styles.menuItemText}>
                  <span className={styles.menuItemLabel}>{t(`items.${item.id}.label`, item.label)}</span>
                  <span className={styles.menuItemDescription}>{t(`items.${item.id}.desc`, item.description)}</span>
                </div>
              </div>
              {item.subitems && (
                <div className={styles.expandIcon}>
                  {expanded[item.id] ? <FaChevronDown /> : <FaChevronRight />}
                </div>
              )}
            </div>
            
            {item.subitems && expanded[item.id] && (
              <div className={styles.submenu}>
                {item.subitems.map((subitem) => (
                  <div
                    key={subitem.id}
                    onClick={() => setSelected(subitem.id)}
                    className={`${styles.submenuItem} ${
                      selected === subitem.id ? styles.activeSubmenuItem : ""
                    }`}
                  >
                    <div className={styles.submenuItemIcon}>{subitem.icon}</div>
                    <div className={styles.submenuItemText}>
                      <span className={styles.submenuItemLabel}>{t(`items.${subitem.labelKey || subitem.id}.label`, subitem.label)}</span>
                      <span className={styles.submenuItemDescription}>{t(`items.${subitem.labelKey || subitem.id}.desc`, subitem.description)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;
